/**
 * Cart Upsell — carousel of add-on offers inside the cart drawer.
 *
 * Markup is rendered by snippets/cart-upsell.liquid inside `.drawer__inner`, so it
 * re-renders on every cart change. This custom element re-initialises automatically
 * each time (a fresh <cart-drawer-upsell> upgrades and runs connectedCallback).
 *
 * Responsibilities:
 *  - Carousel navigation (prev / next / dots).
 *  - Add to cart via /cart/add.js, then apply the discount code via /discount/CODE.
 *  - Re-render the drawer so the added product drops out of the carousel and the
 *    totals/discounts update.
 *
 * Product filtering (already-in-cart, minimum cart value) is handled server-side in
 * Liquid, so there is no cart fetch here beyond the refresh after adding.
 */
class CartDrawerUpsell extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-cart-upsell-track]');
    this.slides = Array.from(this.querySelectorAll('[data-cart-upsell-slide]'));
    this.controls = this.querySelector('[data-cart-upsell-controls]');
    this.dotsWrap = this.querySelector('[data-cart-upsell-dots]');
    this.index = 0;

    if (this.slides.length === 0) return;

    this.buildCarousel();
    this.bindAddButtons();
  }

  get routeRoot() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  buildCarousel() {
    const hasMultiple = this.slides.length > 1;
    if (this.controls) this.controls.hidden = !hasMultiple;

    if (this.dotsWrap) {
      this.dotsWrap.innerHTML = '';
      this.slides.forEach((_, i) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'cart-drawer-v2__upsell-dot';
        dot.setAttribute('aria-label', `Go to offer ${i + 1}`);
        dot.addEventListener('click', () => this.goTo(i));
        this.dotsWrap.appendChild(dot);
      });
    }

    this.querySelector('[data-cart-upsell-prev]')?.addEventListener('click', () => this.goTo(this.index - 1));
    this.querySelector('[data-cart-upsell-next]')?.addEventListener('click', () => this.goTo(this.index + 1));

    // Keep dots in sync when the user swipes natively.
    if (this.track) {
      this.track.addEventListener('scroll', () => {
        const newIndex = Math.round(this.track.scrollLeft / this.track.offsetWidth);
        if (newIndex !== this.index) {
          this.index = newIndex;
          this.updateDots();
        }
      }, { passive: true });
    }

    this.goTo(0);
  }

  goTo(target) {
    const count = this.slides.length;
    this.index = ((target % count) + count) % count;

    if (this.track) {
      this.track.scrollTo({ left: this.index * this.track.offsetWidth, behavior: 'smooth' });
    }

    this.updateDots();
  }

  updateDots() {
    this.dotsWrap
      ?.querySelectorAll('.cart-drawer-v2__upsell-dot')
      .forEach((dot, i) => dot.classList.toggle('is-active', i === this.index));
  }

  bindAddButtons() {
    this.querySelectorAll('[data-cart-upsell-add]').forEach((button) => {
      button.addEventListener('click', () => this.addOffer(button));
    });
  }

  async addOffer(button) {
    if (button.dataset.loading === 'true') return;

    const variantId = Number(button.dataset.variantId);
    if (!variantId) return;

    const discountCode = (button.dataset.discountCode || '').trim();

    button.dataset.loading = 'true';
    button.disabled = true;
    button.classList.add('is-loading');

    try {
      // 1. Apply the discount first so the re-rendered drawer already reflects it.
      //    Redirect to /cart.js (tiny) instead of the homepage, so the cookie is set
      //    reliably without downloading a full themed page.
      if (discountCode) {
        const redirectTo = encodeURIComponent(`${this.routeRoot}cart.js`);
        await fetch(
          `${this.routeRoot}discount/${encodeURIComponent(discountCode)}?redirect=${redirectTo}`,
          { method: 'GET' }
        ).catch(() => {});
      }

      // 2. Add to cart AND get the re-rendered drawer in a SINGLE request via the
      //    Cart API's section rendering — no separate section/cart fetches needed.
      const response = await fetch(`${this.routeRoot}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: variantId, quantity: 1 }],
          sections: ['cart-drawer'],
          sections_url: window.location.pathname,
        }),
      });
      if (!response.ok) throw new Error(`Add to cart failed (${response.status})`);

      const data = await response.json();
      let drawerHTML = data.sections && data.sections['cart-drawer'];

      // Fallback for stores where the Cart API didn't return the section.
      if (!drawerHTML) {
        const sections = await fetch(`${this.routeRoot}?sections=cart-drawer`).then((r) => r.json());
        drawerHTML = sections['cart-drawer'];
      }

      this.renderDrawer(drawerHTML);

      // 3. Verify straight from the rendered HTML — no extra network request.
      if (discountCode && drawerHTML && !drawerHTML.toUpperCase().includes(discountCode.toUpperCase())) {
        this.showNotice(`Added, but the discount “${discountCode}” didn’t apply — please check the code.`);
      }
    } catch (error) {
      console.error('[cart-upsell]', error);
      button.classList.remove('is-loading');
      button.disabled = false;
      delete button.dataset.loading;
    }
  }

  renderDrawer(drawerHTML) {
    if (drawerHTML) {
      const fresh = new DOMParser().parseFromString(drawerHTML, 'text/html');
      const freshInner = fresh.querySelector('.drawer__inner');
      const liveInner = document.querySelector('cart-drawer .drawer__inner');

      if (freshInner && liveInner) {
        liveInner.innerHTML = freshInner.innerHTML;
        liveInner.classList.remove('is-empty');
      }

      // Update the header cart count straight from the rendered drawer.
      const countText = fresh.querySelector('.cart-drawer-v2__count')?.textContent || '';
      const count = parseInt(countText.replace(/\D/g, ''), 10);
      if (!Number.isNaN(count)) this.updateHeaderCount(count);
    }

    const drawer = document.querySelector('cart-drawer');
    if (drawer) {
      drawer.classList.remove('is-empty');
      drawer.classList.add('animate', 'active');
    }
  }

  showNotice(message) {
    const notice = document.querySelector('cart-drawer-upsell [data-cart-upsell-notice]');
    if (!notice) return;
    notice.textContent = message;
    notice.hidden = false;
  }

  updateHeaderCount(itemCount) {
    const countEl = document.querySelector('.header-v2-cart-count span');
    if (countEl && typeof itemCount === 'number') {
      countEl.textContent = itemCount < 100 ? String(itemCount) : '';
    }
  }
}

if (!customElements.get('cart-drawer-upsell')) {
  customElements.define('cart-drawer-upsell', CartDrawerUpsell);
}
